import { useCallback, useEffect, useRef, useState } from 'react';
import type { PatternBased, RuleBased } from '@kkp-fraud/shared';
import { kindOf, type FieldName } from '../fields';
import { useCustomFields } from '../hooks/useCustomFields';
import { t, useLocale } from '../i18n';
import {
  KKP_BLOCK_STYLES,
  KKP_COMPONENT_STYLES,
  KKP_FONT_STYLE,
  blockDefinitions,
  offeredFields,
  toolboxFor,
  type BlockDefinition,
} from './defs';
import {
  HAT_TYPE,
  needsReload,
  ruleToWorkspaceState,
  serializeRuleState,
  workspaceStateToRule,
  type RuleKindShort,
  type WorkspaceState,
} from './state';
import { registerWindowBlocks } from './windowField';

type BlocklyModule = typeof import('blockly/core');
type Workspace = ReturnType<BlocklyModule['inject']>;
/**
 * The Scratch-style view of one rule.
 *
 * Blockly is imported dynamically: the Sentence view — the default, and the only one most
 * sessions open — never downloads it, and the bundler gives it its own chunk.
 *
 * Two-way sync, and the two things that make it not loop:
 *   • Loading the canvas is done with `Events.disable()`, so `workspaces.load` cannot fire
 *     the change listener that would write the rule straight back.
 *   • `lastRef` holds the canonical serialization of the rule the canvas is showing. The
 *     canvas is only reloaded when the incoming rule says something different, and a rule
 *     is only emitted when the blocks say something different — the same `emitted` ref
 *     idiom as `components/ClauseBuilder.tsx:73-82`.
 */

export interface BlocksWorkspaceProps {
  kind: RuleKindShort;
  rule: RuleBased | PatternBased;
  /** Fields the rule's sources can actually send — §1.3: what is unavailable is not offered. */
  allowedFields: readonly FieldName[];
  /** Fields the rule already uses; kept in the dropdowns so a stored value is never mangled. */
  usedFields: readonly FieldName[];
  /** False while the Sentence tab is in front — the canvas stays mounted but has no size. */
  visible: boolean;
  onChange: (rule: RuleBased | PatternBased) => void;
  /** Milliseconds a block edit waits before it reaches the draft. */
  debounceMs?: number;
  /**
   * A canvas that can be looked at, not edited (Blockly's `readOnly` workspace): no
   * toolbox, no drag, no field edits, and nothing is ever emitted — a Viewer's canvas.
   */
  readOnly?: boolean;
  /**
   * Blockly could not be loaded (a failed download, a dev server answering 504). The canvas
   * hides its empty host and says so; the editor switches back to the Sentence view and
   * offers "Try again" (UX review P2-4).
   */
  onLoadError?: () => void;
  /**
   * The editor's "push your edit now" hook (QA-R-14): Apply or close must not wait out the
   * debounce, or an edit made just before the click is lost. The canvas registers a function
   * that closes a field editor still open (committing what was typed), cancels the pending
   * timer and writes the canvas into the rule through `onChange` — true when it changed it.
   */
  registerFlush?: (flush: () => boolean) => () => void;
}

type Status = 'loading' | 'ready' | 'failed';

export function BlocksWorkspace({
  kind,
  rule,
  allowedFields,
  usedFields,
  visible,
  onChange,
  debounceMs = 150,
  readOnly = false,
  onLoadError,
  registerFlush,
}: BlocksWorkspaceProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const blocklyRef = useRef<BlocklyModule | null>(null);
  const wsRef = useRef<Workspace | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The rule the canvas currently shows, serialized — the guard against a sync loop. */
  const lastRef = useRef<string | null>(null);
  /** Latest props, read from callbacks that outlive a render. */
  const ruleRef = useRef(rule);
  const onChangeRef = useRef(onChange);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;
  const optionsRef = useRef({ allowedFields, usedFields });

  /**
   * The palette the rule may use right now. It changes without the rule changing — flipping
   * a row's MASTERCARD/ITMX button narrows the field list while the document stays the same
   * — and the dropdown options are baked into the block definitions, so the canvas has to be
   * rebuilt when it moves. Without this, a rule switched to ITMX-only would still offer
   * `fraud_score` in the blocks, which §1.3 forbids.
   */
  // Wave 6: a custom field's kind is part of its block, so it is part of the key too.
  useCustomFields();
  const offeredKey = offeredFields(allowedFields, usedFields)
    .map((f) => `${f}:${kindOf(f)}`)
    .join('|');
  const offeredKeyRef = useRef(offeredKey);

  const [status, setStatus] = useState<Status>('loading');
  const [warnings, setWarnings] = useState<string[]>([]);
  /**
   * The language switch has to reach Blockly two ways, and neither is a re-render:
   * `Blockly.setLocale` replaces its OWN menus but does not repaint blocks, and our block
   * messages are baked into the definitions at define time. So `locale` is a dependency of
   * the mount effect below — a switch disposes the workspace and injects a fresh one. The
   * draft lives in `AppLayout`, so nothing the user typed is lost by that.
   */
  const locale = useLocale();

  ruleRef.current = rule;
  onChangeRef.current = onChange;
  optionsRef.current = { allowedFields, usedFields };

  /** Define (or redefine) our blocks for the field set this rule may use, then the toolbox. */
  const defineFor = useCallback(
    (B: BlocklyModule): BlockDefinition[] => {
      const { allowedFields: allowed, usedFields: used } = optionsRef.current;
      const defs = blockDefinitions(kind, allowed, used);
      // Redefining a type logs a warning; drop the old entry first so switching rules is quiet.
      for (const def of defs) delete (B.Blocks as Record<string, unknown>)[def.type];
      if (!B.Extensions.isRegistered('kkp_hat')) {
        B.Extensions.register('kkp_hat', function (this: { hat?: string }) {
          this.hat = 'cap';
        });
      }
      registerWindowBlocks(B);
      B.defineBlocksWithJsonArray(defs as unknown[]);
      return defs;
    },
    [kind],
  );

  /**
   * The first condition in a stack has no join to show — `Clause.logic` is ignored on the
   * first clause (`packages/shared/src/index.ts:159`) and the clause rows print a fixed
   * "where" there. Blockly has no conditional field, so the field is hidden on whichever
   * block is currently on top and shown again the moment another one is dropped above it.
   */
  const restyleStack = useCallback(
    (ws: Workspace) => {
      if (kind !== 'rb') return;
      try {
        for (const top of ws.getTopBlocks(false)) {
          let block = top.type === HAT_TYPE.rb ? top.getInputTargetBlock('COND') : null;
          let index = 0;
          while (block) {
            const field = block.getField('LOGIC');
            if (field && field.isVisible() !== (index > 0)) {
              field.setVisible(index > 0);
              (block as { queueRender?: () => void }).queueRender?.();
            }
            block = block.getNextBlock();
            index += 1;
          }
        }
      } catch {
        /* cosmetic only — a rule that reads "and x is …" is still the right rule */
      }
    },
    [kind],
  );

  const loadFromRule = useCallback(
    (B: BlocklyModule, ws: Workspace, next: RuleBased | PatternBased) => {
      const defs = defineFor(B);
      B.Events.disable();
      try {
        ws.clear();
        B.serialization.workspaces.load(ruleToWorkspaceState(next, kind), ws);
      } finally {
        B.Events.enable();
      }
      try {
        // A read-only workspace has no toolbox to update.
        if (!readOnly) (ws as unknown as { updateToolbox?: (d: unknown) => void }).updateToolbox?.(toolboxFor(kind, defs));
      } catch {
        /* the toolbox only narrows the offer; a refusal here must not take the canvas down */
      }
      lastRef.current = serializeRuleState(next, kind);
      restyleStack(ws);
      setWarnings([]);
    },
    [defineFor, kind, readOnly, restyleStack],
  );

  /** Read the canvas and, if it says something new and valid, push it into the draft; true when it did. */
  const flush = useCallback((): boolean => {
    const B = blocklyRef.current;
    const ws = wsRef.current;
    if (!B || !ws || readOnly) return false;
    const state = B.serialization.workspaces.save(ws) as WorkspaceState;
    const result = workspaceStateToRule(state, ruleRef.current, kind);
    setWarnings(result.warnings);
    if (!result.ok) return false;
    const serialized = serializeRuleState(result.rule, kind);
    if (serialized === lastRef.current) return false;
    lastRef.current = serialized;
    onChangeRef.current(result.rule);
    return true;
  }, [kind, readOnly]);

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // ---- the editor asks for the canvas now (Apply, close) -----------------------
  useEffect(() => {
    if (!registerFlush) return;
    return registerFlush(() => {
      const B = blocklyRef.current;
      if (!B || !wsRef.current || readOnly) return false;
      // A field still being typed into: close its editor so the typed value is the field's.
      try {
        if (B.WidgetDiv.isVisible()) B.WidgetDiv.hide();
        B.DropDownDiv.hideWithoutAnimation();
      } catch {
        /* no pop-up open — nothing to commit */
      }
      cancelPending();
      return flush();
    });
  }, [registerFlush, readOnly, cancelPending, flush]);

  // ---- mount: load Blockly, inject, listen ------------------------------------
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const [B, en] = await Promise.all([import('blockly/core'), import('blockly/msg/en')]);
        // `blockly/core` ships no messages; the root `blockly` entry also pulls the whole
        // standard block library, which this console does not use. So: core + messages.
        B.setLocale(en as unknown as Record<string, string>);
        if (locale === 'th') {
          const th = await import('blockly/msg/th');
          B.setLocale(th as unknown as Record<string, string>);
        }
        if (cancelled || !hostRef.current) return;

        const defs = defineFor(B);
        const theme = B.Theme.defineTheme('kkp', {
          name: 'kkp',
          base: B.Themes.Zelos,
          blockStyles: KKP_BLOCK_STYLES,
          componentStyles: KKP_COMPONENT_STYLES,
          fontStyle: KKP_FONT_STYLE,
        });

        // The editor is a modal <dialog> (components/Dialog.tsx): everything outside it is
        // inert, so Blockly's pop-ups — a field's text box, a dropdown, a tooltip, which it
        // appends to <body> by default — would be unclickable. They go into the dialog's
        // `.dlg-layer` instead (at the document origin, so Blockly's page coordinates hold).
        // Blockly makes them once and reuses them, so ones left in <body> are moved in too.
        const dialog = hostRef.current.closest('dialog');
        const layer = dialog ? ([...dialog.children].find((c) => c.classList.contains('dlg-layer')) as HTMLElement | undefined) : undefined;
        B.setParentContainer(layer ?? document.body);

        const ws = B.inject(hostRef.current, {
          renderer: 'zelos',
          theme,
          toolbox: toolboxFor(kind, defs) as unknown as string,
          grid: { spacing: 20, length: 2, colour: '#e3e2ea', snap: true },
          zoom: { controls: true, wheel: false, startScale: 0.9 },
          move: { scrollbars: true, drag: true, wheel: true },
          trashcan: !readOnly,
          sounds: false,
          media: '/blockly-media/',
          readOnly,
        });

        if (cancelled) {
          ws.dispose();
          return;
        }
        if (layer) {
          for (const el of document.querySelectorAll<HTMLElement>('.blocklyWidgetDiv, .blocklyDropDownDiv, .blocklyTooltipDiv')) {
            if (el.parentElement !== layer) layer.appendChild(el);
          }
        }

        blocklyRef.current = B;
        wsRef.current = ws;
        loadFromRule(B, ws, ruleRef.current);

        ws.addChangeListener((event: { isUiEvent?: boolean }) => {
          // Blockly fires a stream of UI events (click, scroll, selection) that change no
          // rule; and `loadFromRule` runs with events disabled, so nothing here is an echo.
          // A read-only canvas never writes back.
          if (event.isUiEvent || readOnly) return;
          restyleStack(ws);
          cancelPending();
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            flush();
          }, debounceMs);
        });

        setStatus('ready');
        B.svgResize(ws);
      } catch {
        if (!cancelled) {
          setStatus('failed');
          onLoadErrorRef.current?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      cancelPending();
      wsRef.current?.dispose();
      wsRef.current = null;
      blocklyRef.current = null;
      lastRef.current = null;
    };
  }, [cancelPending, debounceMs, defineFor, flush, kind, loadFromRule, locale, readOnly, restyleStack]);

  // ---- the rule changed somewhere else (the sentence view) --------------------
  useEffect(() => {
    const B = blocklyRef.current;
    const ws = wsRef.current;
    if (!B || !ws) return;
    const paletteMoved = offeredKeyRef.current !== offeredKey;
    if (!paletteMoved && !needsReload(rule, kind, lastRef.current)) return;
    offeredKeyRef.current = offeredKey;
    cancelPending();
    loadFromRule(B, ws, rule);
  }, [cancelPending, kind, loadFromRule, offeredKey, rule, status]);

  // ---- tab switches ----------------------------------------------------------
  useEffect(() => {
    const B = blocklyRef.current;
    const ws = wsRef.current;
    if (!B || !ws) return;
    if (visible) {
      // Blockly measures the container; while it was hidden that measurement was zero.
      B.svgResize(ws);
      return;
    }
    // Leaving for the sentence view: a debounced edit must not be lost on the way out.
    cancelPending();
    flush();
  }, [cancelPending, flush, status, visible]);

  return (
    <div className="blocks-view" data-testid={`blocks-${kind}`} data-readonly={readOnly || undefined} hidden={!visible}>
      {status === 'loading' && <div className="empty small">{t('view.blocks.loading')}</div>}
      {status === 'failed' && (
        <div className="banner warn" role="alert">
          {t('view.blocks.failed')}
        </div>
      )}
      {/* A failed load leaves no empty 440 px canvas behind (UX review P2-4). */}
      <div ref={hostRef} className="blockly-host" data-testid={`blockly-host-${kind}`} hidden={status === 'failed'} />
      {warnings.length > 0 && (
        <div className="banner warn" role="alert" data-testid={`blocks-warning-${kind}`}>
          <strong>{t('view.blocks.warning')}</strong>
          <ul>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
